// assets/js/hi-tech-animations.js
// Révélations au scroll (GSAP + ScrollTrigger) — optimisé : direction, delay, once, etc.

import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

const DEFAULTS = {
  distance: 24,     // px
  stagger: 0.08,    // s (batch)
  durationIn: 0.9,  // s
  durationOut: 0.5 // s
};
const SELECTORS = {
  reveal: '.js-reveal',
  blur: '.js-reveal-blur'
};

let _inited = false;

function parseNumber(attr, fallback) {
  const v = parseFloat(attr);
  return Number.isFinite(v) ? v : fallback;
}

function dirToOffset(from, dist) {
  switch (from) {
    case 'left':  return { x: -dist, y: 0 };
    case 'right': return { x:  dist, y: 0 };
    case 'up':    return { x: 0, y: -dist };
    case 'down':  return { x: 0, y:  dist };
    default:      return { x: 0, y: dist }; // fallback "fade-up"
  }
}

function presetElements() {
  // Pré-config : place chaque élément d’après ses data-attrs pour que le batch n’ait qu’à remettre x/y à 0
  gsap.utils.toArray(SELECTORS.reveal).forEach((el) => {
    const from     = el.getAttribute('data-reveal-from') || null; // left|right|up|down|null
    const distAttr = el.getAttribute('data-reveal-distance');
    const dist     = parseNumber(distAttr, DEFAULTS.distance);
    const offset   = dirToOffset(from, dist);
    gsap.set(el, {
      opacity: 0,
      x: offset.x,
      y: offset.y,
      willChange: 'transform,opacity',
      force3D: true
    });
  });

  gsap.utils.toArray(SELECTORS.blur).forEach((el) => {
    const distAttr = el.getAttribute('data-reveal-distance');
    const dist     = parseNumber(distAttr, 12);
    gsap.set(el, {
      opacity: 0,
      y: dist,
      filter: 'blur(8px)',
      willChange: 'transform,opacity,filter',
      force3D: true
    });
  });
}

function batchReveal() {
  // Batch standard (js-reveal)
  ScrollTrigger.batch(SELECTORS.reveal, {
    start: 'top 85%',
    onEnter: (batch) => {
      // delay par élément (data-reveal-delay), stagger global (data-reveal-stagger sur le 1er)
      const first   = batch[0];
      const stagger = parseNumber(first?.getAttribute('data-reveal-stagger'), DEFAULTS.stagger);

      gsap.to(batch, {
        opacity: 1,
        x: 0,
        y: 0,
        duration: DEFAULTS.durationIn,
        ease: 'power2.out',
        stagger,
        delay: (i, target) => parseNumber(target.getAttribute('data-reveal-delay'), 0),
        clearProps: 'willChange',
        overwrite: true
      });
    },
    onLeaveBack: (batch) => {
      // respecte data-reveal-once="true" (ne pas rejouer à la remontée)
      const toReverse = batch.filter(el => el.getAttribute('data-reveal-once') !== 'true');
      if (!toReverse.length) return;

      toReverse.forEach((el) => {
        const from     = el.getAttribute('data-reveal-from') || null;
        const distAttr = el.getAttribute('data-reveal-distance');
        const dist     = parseNumber(distAttr, DEFAULTS.distance);
        const { x, y } = dirToOffset(from, dist);
        gsap.to(el, {
          opacity: 0,
          x, y,
          duration: DEFAULTS.durationOut,
          ease: 'power1.out',
          overwrite: true
        });
      });
    }
  });

  // Batch blur
  ScrollTrigger.batch(SELECTORS.blur, {
    start: 'top 85%',
    onEnter: (batch) => {
      const first   = batch[0];
      const stagger = parseNumber(first?.getAttribute('data-reveal-stagger'), DEFAULTS.stagger);
      gsap.to(batch, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: DEFAULTS.durationIn,
        ease: 'power2.out',
        stagger,
        delay: (i, target) => parseNumber(target.getAttribute('data-reveal-delay'), 0),
        clearProps: 'willChange',
        overwrite: true
      });
    },
    onLeaveBack: (batch) => {
      const toReverse = batch.filter(el => el.getAttribute('data-reveal-once') !== 'true');
      if (!toReverse.length) return;

      toReverse.forEach((el) => {
        const dist = parseNumber(el.getAttribute('data-reveal-distance'), 12);
        gsap.to(el, {
          opacity: 0,
          y: dist,
          filter: 'blur(8px)',
          duration: DEFAULTS.durationOut,
          ease: 'power1.out',
          overwrite: true
        });
      });
    }
  });
}

function debounce(fn, ms = 150) {
  let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), ms); };
}

export function initHiTechReveal() {
  if (_inited) {
    // Refresh si déjà initialisé (utile si ré-invoqué)
    ScrollTrigger.refresh();
    return;
  }

  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  if (reduce) {
    // Accessibilité : tout afficher sans animation
    document.querySelectorAll(`${SELECTORS.reveal}, ${SELECTORS.blur}`).forEach((el) => {
      el.style.opacity = 1;
      el.style.transform = 'none';
      el.style.filter = 'none';
    });
    // Nettoie d’éventuels triggers existants
    ScrollTrigger.getAll().forEach(t => t.kill());
    _inited = true;
    return;
  }

  presetElements();
  batchReveal();

  // Refresh debounced (grilles responsives)
  const onResize = debounce(() => ScrollTrigger.refresh(), 180);
  window.addEventListener('resize', onResize, { passive: true });

  _inited = true;
}

export function refreshHiTechReveal() {
  ScrollTrigger?.refresh();
}
